import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useTheme } from 'vuetify'
import { usePrefs } from '@/stores/prefs'

const query = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null
/** The operating system's setting, shared by every caller; apply() keeps it current. */
const systemDark = ref(query?.matches ?? false)

/** Whether the dark theme is on: the user's choice, or the operating system's setting until they make one. */
export function useDarkMode() {
    const prefs = usePrefs()
    const isDark = computed(() => (prefs.theme === 'system' ? systemDark.value : prefs.theme === 'dark'))

    /** Switches to the other theme and remembers it, so the system setting no longer decides. */
    function toggle() {
        prefs.setTheme(isDark.value ? 'light' : 'dark')
    }

    /** Keeps Vuetify's theme in step with the preference and the system setting (call once, from the app root). */
    function apply() {
        const theme = useTheme()
        const follow = (event: MediaQueryListEvent) => (systemDark.value = event.matches)
        query?.addEventListener('change', follow)
        onBeforeUnmount(() => query?.removeEventListener('change', follow))
        watch(isDark, (dark) => theme.change(dark ? 'dark' : 'light'), { immediate: true })
    }

    return { isDark, toggle, apply }
}
